import type { VersionStatus } from "../components/ui/Tag.tsx";
import type { AbilityCard, BattleStage, MatchMode, TopBonusImg } from "./match";
import { sysTotal, type Level } from "./model.ts";
import { fromBig, money, type Money } from "./big.ts";
import { type DiceParams, type FightParams } from "./params.ts";
import type { PettownParams } from "./params.ts";
import type { PettownSlot, PettownStar } from "./pettown.ts";
import type { AdoptPool } from "./adoptPool.ts";
import type { Reward } from "./reward.ts";
import type { CardPack } from "./card.ts";
import type { RivalDef } from "./fixtures.ts";
import { type AuctionMap } from "./auction.ts";
import { diffBlocks, type Change } from "./autosave.ts";
import type { Tile } from "./tiles.ts";
import type { BoardCell } from "./board-cells.ts";
import type { LampDrop } from "./lamp-drops.ts";
import type { SpinCell } from "./board.ts";
import type { Puzzle, PuzzlePrice } from "./puzzle.ts";
import type { DailyRewards } from "./dailyReward.ts";
import type { SocialChannel, SocialEventType } from "./social.ts";
import type { AvatarFrame } from "./frame.ts";
import type { Pet } from "./pet.ts";

/**
 * 配置版本（设计稿 app.jsx 的版本生命周期）。
 *
 * 三态：draft 可编辑；published 只读历史；active 线上生效（同时也只读）。
 * 版本一经保存不可变——「回滚」不是把旧版本设为生效，而是以旧内容开新草稿，
 * 与后端 cfg_version 的 rollback_from 语义一致。
 */
export interface Version {
  id: string;
  status: VersionStatus;
  time: string;
  user: string;
  note: string;
  /** 发布时保存的内容校验值；历史算法或迁移可能影响它，只作交叉核对。 */
  checksum?: string;
  /** 回滚来源版本号；正常草稿为 null */
  from: string | null;
  /** 底子：拷自的已发布版本（服务端 base_version）。不是线上生效版本时，发布会先把线上的改动合进来 */
  base?: string | null;
  /** 最后一次写入内容的人与时刻（UTC 毫秒）；从没写过时为空串 / null */
  updatedBy?: string;
  updatedAtMs?: number | null;
  /** 最近一次设为生效时的生效方式；从没生效过为 null */
  refreshMode?: import("./refresh-mode").RefreshMode | null;
  /** 金额口径（docs/economy-g-model.md §2.4）；缺省按 1 */
  economyModel?: 1 | 2;
  /** model 2 的公共刻度 num/den（十进制串）；model 1 为 null */
  moneyScale?: { num: string; den: string } | null;
  /** 迁移前身的 E（千分位） */
  legacyEMilli?: number | null;
  /** 发布人与时刻（UTC 毫秒）；草稿、没留下发布日志的旧版本为空串 / null */
  publishedBy?: string;
  publishedAtMs?: number | null;
  /** 在这一版上动过手的人（操作日志里落在这一版的记录），按最后一次操作倒序 */
  people?: Array<{ actor: string; count: number; lastAtMs: number }>;
}

/**
 * 一个版本的完整配置快照。后端一版配置横跨全部 cfg_* 内容表，
 * 前端与之对应：关卡之外，全局参数、奖励、卡牌、竞拍都随版本走。
 */
export interface Snapshot {
  levels: Level[];
  /** 副玩法格。来自 /admin/api/config/tiles */
  tiles: Tile[];
  /** spin 盘面。来自 /admin/api/config/versions/:v/spin */
  spins: SpinCell[];
  dice: DiceParams;
  fight: FightParams;
  rewards: Reward[];
  itemPacks: CardPack[];
  idolPacks: CardPack[];
  auctions: AuctionMap;
  /*
   * 任务 / 成就**不在**这里：暂不走配置版本（用户 2026-09-07），页面自取自存，见 AchievementPage。
   */
  /** 写真拼图。idol 名册**不在**这里——它不受配置版本管辖（idol 方案 §0.1） */
  puzzles: Puzzle[];
  /** 全局价目表：seq → 网格 + 碎片价，跨 idol 一致 */
  puzzlePrices: PuzzlePrice[];
  /** 每日奖励：周期档 + 累计档（cfg_daily_reward，整表读写） */
  dailyRewards: DailyRewards;
  /** 「加入我们」渠道（cfg_social_channel，整表读写） */
  socialChannels: SocialChannel[];
  /** 时间线事件类型（cfg_social_event_type）；这张表同时是 C 端上报接口的白名单 */
  socialEventTypes: SocialEventType[];
  /** 头像框名册（cfg_frame，整表读写）；发放走奖励项 kind=frame */
  frames: AvatarFrame[];
  /** 多人匹配棋盘格位表（cfg_board_cell，整表读写）；只有 match 代理有行 */
  boardCells: BoardCell[];
  /** 神灯掉落表（多人匹配） */
  lampDrops?: LampDrop[];
  /** idol 亲密度等级曲线（cfg_intimacy_level，与来源一起整表读写） */
  intimacyLevels: import("./intimacy").IntimacyLevel[];
  /** idol 亲密度来源（cfg_intimacy_source） */
  intimacySources: import("./intimacy").IntimacySource[];
  /** idol 亲密度的全局参数（cfg_main_common 的一列，跟数值参数同一条 PATCH） */
  intimacy: import("./params").IntimacyParams;
  /** idol 关卡相册（cfg_idol_photo，逐行读写，ID 服务端分配）。第 1 张由服务端用拼图完成图合成，不在这里 */
  idolPhotos: import("./idol-album").IdolPhoto[];
  /** idol VIP 照片（cfg_idol_vip_photo，逐行读写） */
  idolVipPhotos: import("./idol-album").IdolVipPhoto[];
  /** idol VIP 视频（cfg_idol_vip_video，逐行读写） */
  idolVipVideos: import("./idol-album").IdolVipVideo[];
  /** 关卡多图（改版）：关卡（cfg_stage 类，逐行读写，ID 服务端分配）与关卡下的图 */
  stages: import("./stage-level").Stage[];
  stageImages: import("./stage-level").StageImage[];
  /** 合成与订单（十一张 cfg_merge_* 整块读写）；老服务端没带时不在快照里 */
  merge?: import("./merge").MergeBlock;
  /** 神灯的全局参数（万能卡保底阈值） */
  lamp: import("./params").LampParams;
  /** 萌宠小镇的四个全局参数（与 dice / fight 同源，都是 cfg_main_common 的列） */
  pettown: PettownParams;
  /** 萌宠小镇主建筑星级表（cfg_pettown_star，稀疏、整表读写）：只有改过什么的星才有行，页面按累计星数上限铺满 */
  pettownStars: PettownStar[];
  /** 萌宠小镇副建筑槽位（cfg_pettown_slot，整表读写） */
  pettownSlots: PettownSlot[];
  /** 多人对战：匹配与房间参数（cfg_match_mode，一玩法一行，整表读写）。单人代理这里是空的 */
  matchModes: MatchMode[];
  /** 多人对战：战阶表（cfg_battle_stage） */
  battleStages: BattleStage[];
  /** 多人对战：道具卡表（cfg_ability_card） */
  abilityCards: AbilityCard[];
  /** 萌宠小镇：营地天赋树节点（cfg_talent_node，整表读写）。空表 = 不开天赋树 */
  talentNodes: import("./talent").TalentNode[];
  /** 喵汪角色（cfg_pet）。**在这里而 idol 不在**：它进配置版本，见 src/db/schema/pet.ts */
  pets: Pet[];
  /** 领养池（cfg_adopt_pool，整表读写）。V1 发布闸强制只有一段 */
  adoptPools: AdoptPool[];
  /** 升星档（cfg_pet_star，整表读写）。空表 = 这一版不显示升星预告；升星功能本身是 V2 */
  petStars: import("./petStar").PetStarStep[];
  /** 宠物升星的五个全局参数（cfg_main_common 的五列，跟数值参数同一条 PATCH） */
  petStar: import("./params").PetStarParams;
  /**
   * 开服前要定的三个选择器（cfg_main_common 的三列，跟数值参数同一条 PATCH，各走各的字段）。
   * gameMode 空串 = 存量未选，两边都放行（服务端 game/game-mode.ts）；
   * buildingSystem 空串 = 还没选，发布闸会拦（它决定关卡制 / 萌宠小镇两组配置哪一组有意义）。
   */
  selectors: { gameMode: string; buildingSet: string; buildingSystem: string; buildingPresentation?: string;
    /** 客户端用哪套造价：formula / manual / rival:<id>；空串 = 还没选 */
    costSource: string; costCurve?: string;
    /** 多人对局最高奖励四种奖励的图（cfg_main_common.top_bonus_img，全局一套）；老快照没有 */
    topBonusImg?: TopBonusImg };
  /**
   * 竞品列定义（cfg_main_rival，随版本存）。关卡制的逐格价在 levels[].rivals[列 id]，
   * 萌宠小镇的逐星价在 pettownRivalPrices。颜色只是画图用，不入库
   */
  rivals: RivalDef[];
  /** 萌宠小镇竞品逐星价：列 id → { 星号 → 价 } */
  pettownRivalPrices: Record<string, Record<number, Money>>;
}

/** 版本号 → 该版本的完整快照。版本不可变，所以快照存下后不再改 */
export type VersionStore = Record<string, Snapshot>;

/** 快照全是可 JSON 序列化的普通对象，深拷贝直接走 JSON——比逐字段手抄不容易漏 */
export const cloneSnap = (s: Snapshot): Snapshot => JSON.parse(JSON.stringify(s)) as Snapshot;

export const nowStr = () => {
  const d = new Date(), p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** 深拷贝关卡数组。存进版本快照前必须断开引用，否则改草稿会连带改历史 */
export function cloneLevels(levels: Level[]): Level[] {
  return levels.map((l) => ({
    ...l,
    i18n: Object.fromEntries(Object.entries(l.i18n).map(([k, v]) => [k, { ...v }])),
    buildings: l.buildings.map((b) => ({ ...b })),
    bldInit: l.bldInit.slice(),
    star: l.star.slice(),
    sys: { ...l.sys },
    rivals: Object.fromEntries(Object.entries(l.rivals).map(([k, v]) => [k, { ...v }])),
  }));
}

/* ---------------- 差异 ---------------- */

/** 参与逐字段对比的关卡字段（数组字段拼成串比较） */
export const DIFF_FIELDS = ["name", "stage", "B", "targetRolls", "targetRollsOverride", "costMatrixSeed", "rollsRange", "actualC", "N", "M", "bldInit", "star"] as const;
export type DiffField = (typeof DIFF_FIELDS)[number];

export type DiffRow =
  | { kind: "field"; field: DiffField; from: string | number | null; to: string | number | null; num: boolean }
  | { kind: "detail"; change: Change }
  | { kind: "total"; from: Money | null; to: Money | null }
  | { kind: "cell"; b: number; l: number; from: Money | null; to: Money | null }
  | { kind: "added"; name: string }
  | { kind: "removed"; name: string };

export interface DiffGroup { level: Level; rows: DiffRow[] }
export interface Diff { groups: DiffGroup[]; cellCount: number }

export const SNAPSHOT_SECTIONS = [
  "dice", "fight", "rewards", "itemPacks", "idolPacks", "auctions", "tiles", "spins", "puzzles", "puzzlePrices", "dailyRewards",
  "socialChannels", "socialEventTypes", "frames", "boardCells", "lampDrops", "lamp", "intimacyLevels", "intimacySources", "intimacy", "idolPhotos", "idolVipPhotos", "idolVipVideos", "stages", "stageImages", "merge", "pets", "adoptPools", "petStars", "petStar", "selectors",
  // 萌宠小镇：体系选择器在 selectors 里，这里是它的内容
  "pettown", "pettownStars", "pettownSlots",
  // 多人对战三张表
  "matchModes", "battleStages", "abilityCards",
  // 萌宠小镇：营地天赋树
  "talentNodes",
  // 竞品列（随版本存）：列定义 + 萌宠小镇逐星价；关卡制逐格价在 levels 里由 diffLevels 比
  "rivals", "pettownRivalPrices",
] as const satisfies ReadonlyArray<Exclude<keyof Snapshot, "levels">>;

/**
 * 比较完整版本快照里除 levels 外的所有块。
 *
 * 以前版本抽屉只把 `levels` 交给 DiffView，因此骰子、卡包、玩法格、spin 等
 * 真实变化都会被宣称为「两个版本内容完全一致」。新增快照块时只需进入 Snapshot + SNAPSHOT_SECTIONS，
 * 界面无需再手写业务判断。
 *
 * 行按 id / code 认（与修改记录同一套，见 autosave 的 diffBlocks）。以前按下标比，
 * 在最前面插一格 spin，后面每一格都会显示成「code、名称、奖励全改了」。
 */
export function diffSnapshot(a: Snapshot, b: Snapshot): Change[] {
  return diffBlocks(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, SNAPSHOT_SECTIONS);
}

const flat = (v: unknown): string | number | null =>
  Array.isArray(v) ? v.map((x) => (x == null ? "—" : x)).join(" / ") : (v as string | number | null);

/** 两个版本的逐关差异。文案不在这里生成——组件按 kind/field 取 i18n key */
export function diffLevels(a: Level[], b: Level[]): Diff {
  const groups: DiffGroup[] = [];
  let cellCount = 0;
  const ids = [...new Set([...a.map((l) => l.id), ...b.map((l) => l.id)])].sort((x, y) => x - y);
  for (const id of ids) {
    const la = a.find((l) => l.id === id), lb = b.find((l) => l.id === id);
    if (!la || !lb) {
      const only = (la ?? lb)!;
      groups.push({ level: only, rows: [{ kind: la ? "removed" : "added", name: only.name }] });
      cellCount += 1;
      continue;
    }
    const rows: DiffRow[] = [];
    for (const f of DIFF_FIELDS) {
      const va = flat(la[f]), vb = flat(lb[f]);
      // 经济字段超过 2^53 时是数字串（big.ts Money），同样按数字显示
      const isNum = (v: unknown) => typeof v === "number" || (typeof v === "string" && /^-?\d+$/.test(v));
      if (va !== vb) rows.push({ kind: "field", field: f, from: va, to: vb, num: isNum(va) || isNum(vb) });
    }
    const ta = sysTotal(la), tb = sysTotal(lb);
    if (ta !== tb) rows.push({ kind: "total", from: ta == null ? null : fromBig(ta), to: tb == null ? null : fromBig(tb) });
    const keys = new Set([...Object.keys(la.sys), ...Object.keys(lb.sys)]);
    for (const k of [...keys].sort()) {
      const va = money(la.sys[k as keyof typeof la.sys]), vb = money(lb.sys[k as keyof typeof lb.sys]);
      if (va !== vb) {
        const [bi, l] = k.split("_").map(Number);
        rows.push({ kind: "cell", b: bi, l, from: va, to: vb });
        cellCount++;
      }
    }
    const extra = [...new Set([...Object.keys(la), ...Object.keys(lb)])]
      .filter((k) => ![...DIFF_FIELDS, "id", "uid", "sys"].includes(k));
    for (const c of diffBlocks(la as unknown as Record<string, unknown>, lb as unknown as Record<string, unknown>, extra)) {
      rows.push({ kind: "detail", change: { ...c, block: "levels", path: `[id=${id}].${c.block}${c.path.startsWith("[") ? "" : c.path ? "." : ""}${c.path}`, row: lb.name || la.name } });
    }
    if (rows.length) groups.push({ level: la, rows });
  }
  return { groups, cellCount: cellCount + groups.reduce((n, g) => n + g.rows.filter((r) => r.kind !== "cell").length, 0) };
}
